import { useEffect, useState } from "react";
import { AppTokenPayload, HubspotAppData, Page, Workflow, WorkflowAutomation, WorkflowAutomationItem } from "./interface";
import { getData } from "./backend";
import AppWorkflowList from "./AppWorkflowList";
import AppWorkflowData from "./AppWorkflowData";
import AppWorkflowAutomation from "./AppWorkflowAutomation";
import AppWorkflowPreview from "./AppWorkflowPreview";
import { useTranslation } from "react-i18next";
import { initI18n } from "./i18n";
import packageJson from '../package.json';
import AppOnboarding from "./AppOnboarding";
import { Toaster } from "./toast";
import posthog from "posthog-js";
import { jwtDecode } from "jwt-decode";

initI18n();

function App() {
  const { t, i18n } = useTranslation();

  const query = new URLSearchParams(window.location.search);
  const token = query.get('token');
  const automationItemId = query.get('item');
  const outputIndexParam = query.get('outputIndex');
  const initialOutputIndex = outputIndexParam !== null && /^\d+$/.test(outputIndexParam) ? Number(outputIndexParam) : undefined;

  const language = query.get('lang') || "en";

  const [currentPage, setCurrentPage] = useState<Page>(automationItemId ? "PREVIEW" : "LIST");
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow>();

  const [currentAutomation, setCurrentAutomation] = useState<WorkflowAutomation>();
  const [currentAutomationItem, setCurrentAutomationItem] = useState<WorkflowAutomationItem>();

  const [appData, setAppData] = useState<HubspotAppData>();
  const [error, setError] = useState<Error>();
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    posthog.init('phc_fARKtNyNUy5nE4GiGbmjcDVbH7NUCH10kLgdS8YHz9Q', {
      api_host: 'https://eu.i.posthog.com',
      defaults: '2025-05-24'
    })

    if (token) {
      const payload = jwtDecode<AppTokenPayload>(token);

      posthog.identify(
        payload["hubspot_user_email"], // TODO: Should we change this to the user id?
        { email: payload["hubspot_user_email"] }
      );
    }
  }, [token]);

  useEffect(() => {
    if (!token || !currentWorkflow?.id)
      return;
    if (currentPage !== "DATA" && currentPage !== "RECIPIENTS")
      return;

    getData(`/hubspot/app/data/${currentWorkflow.id}/`, token)
      .then(data => {
        setAppData(data);
        setCurrentWorkflow(data.workflow);
      })
      .catch(setError);
  }, [token, currentWorkflow?.id, currentPage]);

  const firstAutomationItemId = currentAutomation?.items?.[0]?.id;
  useEffect(() => {
    if (currentPage !== "PREVIEW" || !token)
      return;

    const itemId =
      automationItemId ??
      firstAutomationItemId;

    if (!itemId) {
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    getData(`/hubspot/app/automation-items/${itemId}/`, token)
      .then(async (item: WorkflowAutomationItem) => {
        const workflowId = item.workflow?.id;
        if (!workflowId)
          return { item, workflow: undefined as Workflow | undefined };

        const data = await getData(`/hubspot/app/data/${workflowId}/`, token);
        return { item, workflow: data.workflow as Workflow };
      })
      .then(({ item, workflow }) => {
        if (cancelled) return;
        setCurrentAutomationItem(item);
        if (workflow)
          setCurrentWorkflow(workflow);
        setPreviewLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setPreviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentPage, token, automationItemId, firstAutomationItemId]);

  useEffect(() => {
    if (currentPage === "LIST") {
      setCurrentWorkflow(undefined);
      setAppData(undefined);
    }
  }, [currentPage]);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  function handleSelectWorkflow(workflow: Workflow) {
    setCurrentWorkflow(workflow);
    setCurrentPage("DATA");
  }

  if (window.location.pathname === "/connect" || !token) {
    return <AppOnboarding />
  }

  if (window.location.pathname === "/onboarding") {
    return <AppOnboarding initalStep={1} />
  }

  if (!token) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center">
        <div className="text-lg font-semibold text-gray-600">
          {t("This app is only to be ran from HubSpot")}
        </div>
        <div className="text-sm text-gray-400 font-semibold mt-2">
          v{packageJson.version}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center">
        <div className="text-lg font-semibold text-red">
          {t("Something went wrong. Please contact support.")}
        </div>
        <div className="text-sm text-gray-400 font-semibold mt-2">
          v{packageJson.version}
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen pb-2">
      <Toaster />
      <div className={currentPage === "LIST" ? "h-full" : "hidden"}>
        <AppWorkflowList token={token} onSelectWorkflow={handleSelectWorkflow} />
      </div>
      {(currentPage === "DATA" || currentPage === "RECIPIENTS") &&
        <AppWorkflowData token={token} workflow={currentWorkflow!} appData={appData!} currentPage={currentPage} onClickBack={() => { currentPage === "RECIPIENTS" ? setCurrentPage("DATA") : setCurrentPage("LIST"); }} onClickRecipeints={() => setCurrentPage("RECIPIENTS")} onStartAutomation={(automation) => { setCurrentAutomation(automation); setCurrentPage("AUTOMATION"); }} />
      }
      {currentPage === "AUTOMATION" &&
        <AppWorkflowAutomation token={token} workflow={currentWorkflow!} automation={currentAutomation!} onCompleteAutomation={(automation) => { setCurrentAutomation(automation); setCurrentPage("PREVIEW") }} />
      }
      {currentPage === "PREVIEW" && (
        previewLoading || !currentAutomationItem || !currentWorkflow ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className="w-32 h-32" />
            <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading")}...</div>
          </div>
        ) : (
          <AppWorkflowPreview token={token} workflow={currentWorkflow} automationItem={currentAutomationItem} initialOutputIndex={initialOutputIndex} />
        )
      )}
    </div>
  );
}

export default App;
